import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

import LOGO_WHITE from '../assets/images/Dgitaal_footer_logo.png';
import LOGO_DARK  from '../assets/images/Dgitaal_logo.png';

const dienstenSubItems = [
  { name: 'Microsoft 365',   href: '/diensten/microsoft365' },
  { name: 'Managed Services', href: '/diensten/managed-services' },
  { name: 'Cybersecurity',   href: '/diensten/cybersecurity' },
  { name: 'IT Beheer',       href: '/diensten/it-beheer' },
  { name: 'Telefonie',       href: '/diensten/telefonie' },
  { name: 'E-facturatie',    href: '/diensten/e-facturatie' },
  { name: 'Backups',         href: '/diensten/backups' },
  { name: 'Monitoring',      href: '/diensten/monitoring' },
  { name: 'Hosting',         href: '/diensten/hosting' },
];

const navLinks = [
  { name: 'Home',               href: '/' },
  { name: 'Bedrijfsoplossingen', href: '/bedrijfsoplossingen' },
  { name: 'Referenties',        href: '/referenties' },
  { name: 'Partners',           href: '/partners' },
  { name: 'Cloud',              href: '/cloud' },
  { name: 'Hardware',           href: '/hardware' },
  { name: 'Nieuws',             href: '/nieuws' },
  { name: 'Jobs',               href: '/jobs' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [scrolled,   setScrolled]     = useState(false);
  const [dienstenOpen, setDienstenOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isDienstenActive = location.pathname.startsWith('/diensten');
  const isDienstenSubActive = (path) => location.pathname === path;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => setMobileOpen(false), [location]);

  const isHome = location.pathname === '/';
  const navBg = isHome
    ? (scrolled ? 'bg-[#16224b] shadow-lg shadow-black/20' : 'bg-transparent')
    : 'bg-[#16224b] shadow-lg shadow-black/20';

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="header">
      <nav
        className={`transition-all duration-400 ${navBg}`}
        data-testid="main-nav"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 z-10" data-testid="logo-link">
              <img
                src={scrolled ? LOGO_WHITE : LOGO_WHITE}
                alt="Dgitaal"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link px-4 py-2 text-sm font-medium transition-colors rounded
                    ${isActive(link.href)
                      ? 'text-[#f8d45a]'
                      : 'text-white/85 hover:text-white'
                    }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Diensten dropdown */}
              <DropdownMenu open={dienstenOpen} onOpenChange={setDienstenOpen}>
                <DropdownMenuTrigger
                  className={`nav-link flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded outline-none
                    ${isDienstenActive ? 'text-[#f8d45a]' : 'text-white/85 hover:text-white'}`}
                  data-testid="nav-diensten-dropdown"
                >
                  Diensten
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${dienstenOpen ? 'rotate-180' : ''}`} />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="bg-[#233a80] border border-white/10 min-w-[250px] shadow-2xl p-2 rounded-lg"
                  sideOffset={8}
                >
                  <DropdownMenuItem asChild>
                    <Link
                      to="/diensten"
                      className={`flex items-center gap-2 px-3 py-2.5 text-sm font-semibold rounded cursor-pointer transition-colors ${
                        location.pathname === '/diensten'
                          ? 'bg-white/15 text-white'
                          : 'text-white hover:bg-white/10'
                      }`}
                      data-testid="nav-diensten-overzicht"
                    >
                      Alle Diensten
                    </Link>
                  </DropdownMenuItem>
                  <div className="h-px bg-white/10 my-1" />
                  {dienstenSubItems.map((item) => (
                    <DropdownMenuItem key={item.name} asChild>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded cursor-pointer transition-colors ${
                          isDienstenSubActive(item.href)
                            ? 'bg-white/15 text-white'
                            : 'text-white/80 hover:text-white hover:bg-white/10'
                        }`}
                        data-testid={`nav-diensten-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link px-4 py-2 text-sm font-medium transition-colors rounded
                    ${isActive(link.href)
                      ? 'text-[#f8d45a]'
                      : 'text-white/85 hover:text-white'
                    }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA button – desktop */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="btn-gradient text-sm px-6 py-3"
                data-testid="header-cta"
              >
                Start een project
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile full-screen menu */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-[#233a80] flex flex-col pt-24 px-8 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <button
              className="absolute top-6 right-6 text-white"
              onClick={() => setMobileOpen(false)}
            >
              <X className="w-7 h-7" />
            </button>

            <nav className="flex flex-col gap-1 text-lg">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`py-3 font-medium border-b border-white/10 transition-colors
                    ${isActive(link.href) ? 'text-[#f8d45a]' : 'text-white hover:text-[#f8d45a]'}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/diensten"
                onClick={() => setMobileOpen(false)}
                className={`py-3 font-medium border-b border-white/10 transition-colors ${
                  isDienstenActive ? 'text-[#f8d45a]' : 'text-white hover:text-[#f8d45a]'
                }`}
              >
                Diensten
              </Link>
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`py-3 font-medium border-b border-white/10 transition-colors
                    ${isActive(link.href) ? 'text-[#f8d45a]' : 'text-white hover:text-[#f8d45a]'}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="mt-2 mb-1 p-2 rounded-lg border border-white/10 bg-white/5">
                {dienstenSubItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 px-3 rounded text-sm transition-colors ${
                      isDienstenSubActive(item.href)
                        ? 'bg-white/15 text-white'
                        : 'text-white/75 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="mt-6 btn-gradient text-center text-base"
              >
                Start een project <ArrowRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </nav>
    </header>
  );
}
